import { memo } from 'react';
import { Button, Modal, ModalBody, ModalContent } from '@heroui/react';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { IconClose } from '../icons';

export type CloseChatConfirmModalProps = {
  isOpen: boolean;
  chatTitle: string;
  isSubmitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

export const CloseChatConfirmModal = memo(
  ({
    isOpen,
    chatTitle,
    isSubmitting,
    errorMessage,
    onClose,
    onConfirm,
  }: CloseChatConfirmModalProps) => {
    useModalAutoFocus(isOpen);

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        placement="center"
        hideCloseButton
        classNames={{
          base: 'mx-4 w-full max-w-[502px] rounded-[16px] bg-white shadow-[0_24px_48px_rgba(30,30,30,0.12)]',
          body: 'p-0',
          wrapper: 'px-2',
          backdrop: 'bg-black/30',
        }}
      >
        <ModalContent>
          <ModalBody>
            <div className="relative px-6 py-7 sm:px-8">
              <button
                type="button"
                className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-full text-[#757575] transition-colors hover:bg-black/[0.04] hover:text-[#1E1E1E] sm:right-5 sm:top-5"
                onClick={onClose}
                aria-label="Close close chat modal"
                disabled={isSubmitting}
              >
                <IconClose className="h-[18px] w-[18px]" />
              </button>

              <div className="flex flex-col items-center pt-2 text-center">
                <h2 className="w-full max-w-[392px] text-[30px] font-medium leading-[1.2] tracking-normal text-[#1E1E1E]">
                  Are you sure?
                </h2>
                <p className="mt-4 max-w-[420px] text-sm leading-5 text-grey-secondary">
                  You are about to close the chat &ldquo;{chatTitle}&rdquo;. Participants will not
                  be able to send new messages.
                </p>
              </div>

              {errorMessage ? (
                <p className="mt-5 rounded-[8px] bg-[#FFF1F1] px-4 py-3 text-center text-sm font-medium text-red-tertiary">
                  {errorMessage}
                </p>
              ) : null}

              <div className="mt-8 flex items-center justify-center gap-2">
                <Button
                  variant="bordered"
                  radius="sm"
                  className="h-12 w-[164px] rounded-[8px] border-[#1E1E1E] bg-white px-4 py-2.5 text-sm font-semibold text-[#1E1E1E]"
                  onPress={onClose}
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  radius="sm"
                  className="h-12 w-[164px] rounded-[8px] bg-[#900B09] px-4 py-2.5 text-sm font-semibold text-white"
                  isLoading={isSubmitting}
                  onPress={onConfirm}
                >
                  Close
                </Button>
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  },
);

CloseChatConfirmModal.displayName = 'CloseChatConfirmModal';
